import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import CookieBanner from "@/components/marketing/CookieBanner";

import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Individuals from "@/pages/Individuals";
import Investors from "@/pages/Investors";
import Careers from "@/pages/Careers";
import Contact from "@/pages/Contact";
import Integrations from "@/pages/Integrations";
import Legal from "@/pages/Legal";
import OurStory from "@/pages/OurStory";
import ScrollToTop from "@/components/ScrollToTop";
import Features from "@/pages/Features";
import Resources from "@/pages/Resources";
import Users from "@/pages/Users";
import Enterprise from "@/pages/Enterprise";
import Charities from "@/pages/Charities";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import Onboarding from "@/pages/Onboarding";
import AuthCallback from "@/pages/AuthCallback";

import AppShell from "@/components/app/AppShell";
import Dashboard from "@/pages/app/Dashboard";
import Workspace from "@/pages/app/Workspace";
import HierarchyPage from "@/pages/app/HierarchyPage";
import Reports from "@/pages/app/Reports";
import Settings from "@/pages/app/Settings";
import Billing from "@/pages/app/Billing";
import Extension from "@/pages/app/Extension";
import AppIntegrations from "@/pages/app/AppIntegrations";
import Support from "@/pages/app/Support";
import AdminControl from "@/pages/app/AdminControl";

// Captures #session_id= fragments from Emergent Google OAuth before route matching.
function OAuthGate({ children }) {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <OAuthGate>
        <Routes>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/features" element={<Features />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/users" element={<Users />} />
          <Route path="/individuals" element={<Individuals />} />
          <Route path="/use-cases" element={<Navigate to="/individuals" replace />} />
          <Route path="/about" element={<Navigate to="/our-story" replace />} />
          <Route path="/investors" element={<Investors />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/legal/:doc?" element={<Legal />} />
          <Route path="/our-story" element={<OurStory />} />
          <Route path="/faq" element={<Navigate to="/resources#faqs" replace />} />
          <Route path="/enterprise" element={<Enterprise />} />
          <Route path="/charities" element={<Charities />} />

          {/* Auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Onboarding */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />

          {/* App (protected) */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="workspace" element={<Workspace />} />
            <Route path="hierarchy" element={<HierarchyPage />} />
            <Route path="reports" element={<Reports />} />
            <Route path="settings" element={<Settings />} />
            <Route path="billing" element={<Billing />} />
            <Route path="extension" element={<Extension />} />
            <Route path="integrations" element={<AppIntegrations />} />
            <Route path="support" element={<Support />} />
            <Route path="admin" element={<AdminControl />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </OAuthGate>
        <CookieBanner />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
